"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_WORLD_OBJECTS = exports.MAX_WORLD_SIZE = exports.MIN_WORLD_SIZE = void 0;
exports.tileIndex = tileIndex;
exports.inBounds = inBounds;
exports.tileAt = tileAt;
exports.objectAt = objectAt;
exports.isBlocked = isBlocked;
exports.isWalkable = isWalkable;
exports.setTile = setTile;
exports.canPlace = canPlace;
exports.moveObject = moveObject;
exports.addObject = addObject;
exports.removeObject = removeObject;
exports.nextObjectId = nextObjectId;
exports.setSpawn = setSpawn;
exports.encodeTiles = encodeTiles;
exports.decodeTiles = decodeTiles;
exports.normalizeWorld = normalizeWorld;
exports.findSpawn = findSpawn;
const objects_1 = require("./objects");
const types_1 = require("./types");
/** Guard rails for hand-edited or hostile world payloads. */
exports.MIN_WORLD_SIZE = 4;
exports.MAX_WORLD_SIZE = 64;
exports.MAX_WORLD_OBJECTS = 200;
function tileIndex(world, x, y) {
    return y * world.width + x;
}
function inBounds(world, x, y) {
    return x >= 0 && y >= 0 && x < world.width && y < world.height;
}
/** The tile at (x, y). Everything outside the map reads as `wall`, so callers never special-case edges. */
function tileAt(world, x, y) {
    if (!inBounds(world, x, y))
        return 'wall';
    return world.tiles[tileIndex(world, x, y)] ?? 'wall';
}
/** The topmost object covering (x, y) — later entries win, so the last one placed is the one you grab. */
function objectAt(world, x, y) {
    for (let i = world.objects.length - 1; i >= 0; i--) {
        const object = world.objects[i];
        if ((0, objects_1.objectCovers)(object, x, y))
            return object;
    }
    return null;
}
/**
 * Whether the pet is refused entry to (x, y).
 *
 * The single source of truth for collision: movement, the editor's "can this
 * object go here" check and the spawn validator all come through here, so they
 * cannot disagree about what is solid.
 */
function isBlocked(world, x, y) {
    if (!inBounds(world, x, y))
        return true;
    if (tileAt(world, x, y) === 'wall')
        return true;
    for (const object of world.objects) {
        if ((0, objects_1.specOf)(object).solid && (0, objects_1.objectCovers)(object, x, y))
            return true;
    }
    return false;
}
function isWalkable(world, x, y) {
    return !isBlocked(world, x, y);
}
// ------------------------------------------------------------------- editing
//
// Every editor operation is a pure function returning a new world. Dev mode can
// therefore keep a draft in React state, compare it with the saved one to know
// whether anything is unsaved, and throw it away on revert without bookkeeping.
/** Paints one tile. Returns the same world when the tile already has that kind. */
function setTile(world, x, y, kind) {
    if (!inBounds(world, x, y))
        return world;
    const index = tileIndex(world, x, y);
    if (world.tiles[index] === kind)
        return world;
    const tiles = world.tiles.slice();
    tiles[index] = kind;
    return { ...world, tiles };
}
/** Whether `object` would fit with its top-left at (x, y) — inside the map and off the walls. */
function canPlace(world, object, x, y) {
    const spec = (0, objects_1.specOf)(object);
    if (x < 0 || y < 0 || x + spec.width > world.width || y + spec.height > world.height)
        return false;
    for (let dy = 0; dy < spec.height; dy++) {
        for (let dx = 0; dx < spec.width; dx++) {
            if (tileAt(world, x + dx, y + dy) === 'wall')
                return false;
        }
    }
    return true;
}
/**
 * Moves an object's top-left corner to (x, y).
 *
 * Overlapping other furniture is allowed — stacking a lamp on a table is a
 * feature, and refusing it would make the editor fight the person using it.
 * Walls and the map edge are refused, because those cannot be undone by
 * dragging the thing back out again.
 */
function moveObject(world, id, x, y) {
    const object = world.objects.find((candidate) => candidate.id === id);
    if (!object || !canPlace(world, object, x, y))
        return world;
    if (object.x === x && object.y === y)
        return world;
    return {
        ...world,
        objects: world.objects.map((candidate) => candidate.id === id ? { ...candidate, x, y } : candidate),
    };
}
function addObject(world, object) {
    if (world.objects.length >= exports.MAX_WORLD_OBJECTS)
        return world;
    if (!canPlace(world, object, object.x, object.y))
        return world;
    return { ...world, objects: [...world.objects, object] };
}
function removeObject(world, id) {
    const objects = world.objects.filter((object) => object.id !== id);
    return objects.length === world.objects.length ? world : { ...world, objects };
}
/** A fresh object id that no object in `world` is using. */
function nextObjectId(world, kind) {
    const used = new Set(world.objects.map((object) => object.id));
    for (let n = 1;; n++) {
        const id = `${kind}-${n}`;
        if (!used.has(id))
            return id;
    }
}
function setSpawn(world, x, y) {
    if (!inBounds(world, x, y))
        return world;
    return { ...world, spawn: { x, y } };
}
// ------------------------------------------------------------- serialisation
/**
 * The tile grid as one line of ASCII per row.
 *
 * Maps are authored, stored and reviewed in this form: a wall moved by one tile
 * is a one-character diff instead of a reshuffled array of 192 strings.
 */
function encodeTiles(world) {
    const rows = [];
    for (let y = 0; y < world.height; y++) {
        let row = '';
        for (let x = 0; x < world.width; x++) {
            row += types_1.TILE_CHARS[world.tiles[y * world.width + x] ?? 'wall'];
        }
        rows.push(row);
    }
    return rows;
}
const KIND_BY_CHAR = new Map(types_1.TILE_KINDS.map((kind) => [types_1.TILE_CHARS[kind], kind]));
/**
 * Reads an ASCII map back into a tile array.
 *
 * Short rows are padded and unknown characters become floor, so a typo in a
 * hand-written map yields a playable room rather than a crash on load.
 */
function decodeTiles(rows, width, height) {
    const tiles = [];
    for (let y = 0; y < height; y++) {
        const row = rows[y] ?? '';
        for (let x = 0; x < width; x++) {
            tiles.push(KIND_BY_CHAR.get(row[x] ?? '') ?? 'floor');
        }
    }
    return tiles;
}
/**
 * Forces an untrusted world into a shape the rest of the code can rely on.
 *
 * The world round-trips through the database as JSON that dev mode wrote, so
 * "the tile array is the right length" is an assumption, not a fact. This is
 * where it becomes one: dimensions are clamped, the grid is resized rather than
 * rejected, objects that fell off the map are dropped, and the spawn is pulled
 * back to somewhere the pet can actually stand.
 */
function normalizeWorld(world) {
    const width = clampSize(world.width);
    const height = clampSize(world.height);
    const tiles = [];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const source = y < world.height && x < world.width ? world.tiles[y * world.width + x] : null;
            tiles.push(source ?? 'floor');
        }
    }
    const sized = { ...world, width, height, tiles };
    const seen = new Set();
    const objects = world.objects
        .filter((object) => {
        if (seen.has(object.id))
            return false;
        seen.add(object.id);
        return canPlace(sized, object, object.x, object.y);
    })
        .slice(0, exports.MAX_WORLD_OBJECTS);
    const placed = { ...sized, objects };
    return { ...placed, spawn: findSpawn(placed, world.spawn) };
}
function clampSize(value) {
    const size = Math.floor(value);
    if (!Number.isFinite(size))
        return exports.MIN_WORLD_SIZE;
    return Math.min(exports.MAX_WORLD_SIZE, Math.max(exports.MIN_WORLD_SIZE, size));
}
/**
 * `preferred` if the pet can stand there, otherwise the nearest tile it can.
 *
 * Walling in the spawn point is the one edit that could lock a player out of
 * their own home, so it is repaired on the way in rather than enforced by
 * refusing the save.
 */
function findSpawn(world, preferred) {
    if (isWalkable(world, preferred.x, preferred.y))
        return { x: preferred.x, y: preferred.y };
    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let y = 0; y < world.height; y++) {
        for (let x = 0; x < world.width; x++) {
            if (!isWalkable(world, x, y))
                continue;
            const distance = Math.abs(x - preferred.x) + Math.abs(y - preferred.y);
            if (distance < bestDistance) {
                best = { x, y };
                bestDistance = distance;
            }
        }
    }
    // A world with no walkable tile at all is possible — someone painted every
    // tile a wall — and the pet has to be somewhere, so it stands in the corner.
    return best ?? { x: 0, y: 0 };
}
//# sourceMappingURL=world.js.map